import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

// Single cinematic, finance-forward backdrop (Dubai skyline), heavily darkened
const HERO_IMG =
    "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=2400&q=80";

const STATS = [
    { k: "From", v: "4.79%", s: "APR · Credit", color: "text-gold-soft" },
    { k: "Deployed '25", v: "AED 3.4B", s: "Across mandates", color: "text-cream" },
    { k: "Approval", v: "24h", s: "Median", color: "text-coral-soft" },
    { k: "Mandates", v: "1,280+", s: "Active clients", color: "text-gold-soft" },
];

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[82svh] flex flex-col justify-end bg-ink text-cream overflow-hidden"
        >
            {/* Full-bleed background */}
            <div className="absolute inset-0">
                <img
                    src={HERO_IMG}
                    alt="Dubai skyline backdrop"
                    className="w-full h-full object-cover slow-zoom opacity-90"
                />
                {/* Legibility + mood overlays */}
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
                {/* Atmospheric accent glows */}
                <div className="absolute top-1/4 -right-20 w-[480px] h-[480px] bg-gold/15 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 left-1/4 w-[420px] h-[420px] bg-forest/25 rounded-full blur-3xl pointer-events-none" />
            </div>

            {/* Hairline frame */}
            <div className="absolute inset-4 md:inset-6 border border-white/10 pointer-events-none" />

            <div className="relative max-w-[1280px] w-full mx-auto px-6 md:px-10 pt-36 pb-12 md:pt-40 md:pb-16">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                    className="max-w-3xl"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="w-10 h-px bg-gold" />
                        <span
                            data-testid="hero-eyebrow"
                            className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold"
                        >
                            Exclusive Capital · United Arab Emirates
                        </span>
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.0] tracking-tight font-medium text-white"
                    >
                        Liquidity that<br />
                        matches the{" "}
                        <em className="italic text-gold font-normal">tempo</em><br />
                        of your ambition.
                    </h1>

                    <p
                        data-testid="hero-subhead"
                        className="mt-8 text-lg md:text-xl text-cream/80 max-w-xl font-light leading-relaxed"
                    >
                        QuickFin orchestrates discreet, bespoke financing for visionaries, family offices and category-defining founders across the Emirates — credit, capital, mortgage and venture.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                        <a
                            href="#contact"
                            data-testid="hero-cta-primary"
                            className="group inline-flex items-center gap-3 bg-gold text-white px-7 py-4 font-mono text-[12px] uppercase tracking-[0.25em] hover:bg-gold-soft transition-all"
                        >
                            Apply Privately
                            <span className="group-hover:translate-x-1 transition-transform">→</span>
                        </a>
                        <a
                            href="#services"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center gap-3 border border-white/30 text-cream px-7 py-4 font-mono text-[12px] uppercase tracking-[0.25em] hover:border-gold hover:text-gold transition-all"
                        >
                            Explore Mandates
                        </a>
                    </div>
                </motion.div>

                {/* Stats band */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.25 }}
                    className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-white/15 max-w-4xl"
                >
                    {STATS.map((d) => (
                        <div key={d.k} data-testid={`hero-stat-${d.k.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">{d.k}</div>
                            <div className={`font-serif text-3xl md:text-4xl mt-1.5 ${d.color}`}>{d.v}</div>
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cream/50 mt-1">{d.s}</div>
                        </div>
                    ))}
                </motion.div>
            </div>

            <div className="relative z-10 flex justify-center pb-5 text-cream/50">
                <ArrowDown size={16} className="animate-bounce" />
            </div>
        </section>
    );
}
